import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { PageFrame } from '@/components/shared/PageFrame'
import { Trans } from 'react-i18next'
import i18next from 'i18next'

import {
  bisListQueryOptions,
  bisRestocksQueryOptions,
  emailTemplatesQueryOptions,
  reportsBisAggregateMetricsQueryOptions,
  settingsQueryOptions,
  shopifyProductQueryOptions,
} from '@/common/queryOptions'
import { Banner, BlockStack, Box, Button, Card, Layout, SkeletonPage, Text } from '@shopify/polaris'
import { BackInStockContactsTable } from '@/components/backInStock/BackInStockContactsTable'
import { SectionTitle } from '@/components/shared/SectionTitle'
import { BackInStockRestocksTable } from '@/components/backInStock/BackInStockRestocksTable'
import { SkeletonTable } from '@/components/shared/SkeletonTable'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useMutateBisRestock } from '@/hooks/useMutateBisRestock'
import { useMutateBisExport } from '@/hooks/useMutateBisExport'
import { BackInStockProductChart } from '@/components/backInStock/BackInStockProductChart'
import { SkeletonChart } from '@/components/shared/SkeletonChart'
import { useState } from 'react'
import { BackInStockPreview } from '@/components/emailTemplates/BackInStock'
import { openBeacon } from '@/common/functions'

export const Route = createFileRoute('/back-in-stock/products/$productId/')({
  loader: async ({ params: { productId }, context: { queryClient }, route: { path } }) => {
    try {
      await Promise.all([
        queryClient.ensureQueryData(settingsQueryOptions()),
        queryClient.ensureQueryData(emailTemplatesQueryOptions()),
        queryClient.ensureQueryData(shopifyProductQueryOptions({ productId: `gid://shopify/Product/${productId}` })),
        queryClient.ensureQueryData(bisRestocksQueryOptions({ id: productId, page: 1, limit: 5 })),
        queryClient.ensureQueryData(reportsBisAggregateMetricsQueryOptions({ productId })),
        queryClient.ensureQueryData(bisListQueryOptions({ id: productId, page: 1, sendStatus: undefined })),
      ])
    } catch (error) {
      console.error('Error in page loader.', path, error)
      throw error
    }
  },
  pendingComponent: Pending,
  component: Page,
})

function Page() {
  const navigate = useNavigate()
  const { productId } = useParams({ strict: false }) as { productId: string }
  const [isAwaitingRestock, setIsAwaitingRestock] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [
    { data: settings },
    { data: emails },
    {
      data: { product: shopifyProduct },
    },
    {
      data: { data: restocks },
    },
  ] = useSuspenseQueries({
    queries: [
      settingsQueryOptions(),
      emailTemplatesQueryOptions(),
      shopifyProductQueryOptions({ productId: `gid://shopify/Product/${productId}` }),
      bisRestocksQueryOptions({ id: productId, page: 1, limit: 5 }),
    ],
  })

  const exportBisMutation = useMutateBisExport({ id: productId })
  const restockMutation = useMutateBisRestock({ id: productId })

  const hasCustomEmailTemplatesFeature = settings.featureCustomEmailTemplates
  const isRestockInProgress = restocks?.some((restock) => restock.status !== 'COMPLETE' && restock.status !== 'ERROR')

  const sendNotification = async () => {
    setErrorMessage(null)

    try {
      await restockMutation.mutateAsync({
        id: productId,
      })

      setIsAwaitingRestock(true)
    } catch (error) {
      console.log(error)
      setErrorMessage(i18next.t('backInStockRestockSubmissionError'))
    } finally {
      const sendNotificationModal = document.getElementById('send-notification-modal') as UIModalElement
      sendNotificationModal.hide()
    }
  }

  return (
    <PageFrame
      title={shopifyProduct.title}
      backAction={{
        content: i18next.t('backInStockDashTitle'),
        onAction: async () => {
          /* @ts-ignore */
          await shopify.saveBar.leaveConfirmation()

          navigate({ to: '/back-in-stock' })
        },
      }}
      primaryAction={
        <Button
          variant="primary"
          disabled={restockMutation.isPending || isRestockInProgress || isExporting}
          loading={restockMutation.isPending || isExporting}
          onClick={async () => {
            const sendNotificationModal = document.getElementById('send-notification-modal') as UIModalElement
            sendNotificationModal.show()
          }}
        >
          {i18next.t('backInStockProductSendEmailCta')}
        </Button>
      }
      actionGroups={[
        {
          title: i18next.t('genericMoreActions'),
          actions: [
            {
              content: i18next.t('genericViewProduct'),
              url: `shopify://admin/products/${productId}`,
              target: '_blank',
            },
            {
              content: i18next.t('backInStockProductViewBreakdownCta'),
              onAction: () => {
                navigate({ to: '/back-in-stock/products/$productId/variants', params: { productId } })
              },
            },
            {
              content: i18next.t('backInStockExportList'),
              disabled: isExporting,
              onAction: async () => {
                setIsExporting(true)

                await exportBisMutation.mutateAsync({
                  id: productId,
                })

                setIsExporting(false)
              },
            },
          ],
        },
      ]}
    >
      {errorMessage && (
        <Layout.Section>
          <Banner
            tone="critical"
            title={i18next.t('backInStockRestockSubmissionErrorTitle')}
            action={{
              content: i18next.t('genericContactSupport'),
              onAction: async () => {
                openBeacon('ASK')
              },
            }}
            onDismiss={() => {
              setErrorMessage(null)
            }}
          >
            {errorMessage}
          </Banner>
        </Layout.Section>
      )}
      <Layout.Section>
        <BackInStockProductChart productId={productId} />
      </Layout.Section>
      <Layout.Section>
        <SectionTitle title={i18next.t('backInStockContactTableTitle')} description={i18next.t('backInStockContactTableSubtitle')} />
        <BackInStockContactsTable productId={productId} />
      </Layout.Section>
      <Layout.Section>
        <SectionTitle title={i18next.t('backInStockRestockTableTitle')} description={i18next.t('backInStockRestockTableSubtitle')} />
        <BackInStockRestocksTable productId={productId} isAwaitingRestock={isAwaitingRestock} setIsAwaitingRestock={setIsAwaitingRestock} />
      </Layout.Section>

      <ui-modal id="send-notification-modal" variant="base">
        <Box padding="400">
          <div className="modalBannerCard">
            <Card padding="0">
              <BlockStack gap="400">
                <Text as="p">
                  <Trans t={i18next.t} i18nKey="backInStockRestockModalDescription">
                    . <strong> . </strong> .
                  </Trans>
                </Text>
                <Banner tone="warning">
                  <p>{i18next.t('backInStockRestockModalWarningBanner')}</p>
                </Banner>
              </BlockStack>
            </Card>
            <BackInStockPreview
              shopName={settings.shopName}
              templateHtml={
                hasCustomEmailTemplatesFeature
                  ? emails.backInStock.hasCustomTemplate
                    ? emails.backInStock.templateHtml
                    : emails.backInStock.defaultTemplateHtml
                  : emails.backInStock.defaultTemplateHtml
              }
              body={emails.backInStock.body}
              title={emails.backInStock.title}
              primaryButtonText={emails.backInStock.primaryButtonText}
              productName={shopifyProduct.title}
              productImage={shopifyProduct.featuredImage?.url}
              productImageAlt={shopifyProduct.featuredImage?.altText}

            />
          </div>
        </Box>
        <ui-title-bar title={i18next.t('backInStockRestockModalTitle')}>
          <button type="button" variant="primary" onClick={sendNotification} loading={restockMutation.isPending ? '' : undefined}>
            {i18next.t('backInStockRestockModalCta')}
          </button>
          <button
            type="button"
            onClick={() => {
              const sendNotificationModal = document.getElementById('send-notification-modal') as UIModalElement
              sendNotificationModal.hide()
            }}
          >
            {i18next.t('backInStockRestockModalCancelCta')}
          </button>
        </ui-title-bar>
      </ui-modal>
    </PageFrame>
  )
}

function Pending() {
  return (
    <SkeletonPage primaryAction backAction>
      <Layout>
        <Layout.Section>
          <SkeletonChart />
        </Layout.Section>
        <Layout.Section>
          <SectionTitle title={i18next.t('backInStockContactTableTitle')} description={i18next.t('backInStockContactTableSubtitle')} />
          <SkeletonTable rows={5} />
        </Layout.Section>
        <Layout.Section>
          <SectionTitle title={i18next.t('backInStockRestockTableTitle')} description={i18next.t('backInStockRestockTableSubtitle')} />
          <SkeletonTable rows={3} />
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  )
}